import { TextField } from '@efexteam/components';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import usePasswordValidation from './hooks/usePasswordValidation';
import styles from './PasswordInput.style';
import PasswordValidationList from './PasswordValidationList';
import { PasswordInputProps } from './types';

const PasswordInput = ({
  control,
  setValue,
  name,
  confirmFieldName,
  label,
  confirmLabel,
  showValidations = true,
  error,
  errorMessage,
  confirmError,
  confirmErrorMessage,
  validationSchema,
  enforceValidation = false,
}: PasswordInputProps) => {
  const { t } = useTranslation();

  const {
    showPassword,
    showConfirmPassword,
    isPasswordValid,
    isConfirmed,
    showValidationMessages,
    toggleShowPassword,
    toggleShowConfirmPassword,
    validatePassword,
    validateConfirmPassword,
    showValidations: showValidationList,
    hideValidations: hideValidationList,
  } = usePasswordValidation({
    setValue,
    passwordFieldName: name,
    confirmPasswordFieldName: confirmFieldName,
    validationSchema,
    enforceValidation,
  });

  return (
    <>
      <FormControl fullWidth>
        <Controller
          name={name}
          control={control}
          rules={{ required: true }}
          render={({ field: { ref, ...fieldProps } }) => (
            <TextField
              {...fieldProps}
              id={name}
              type={showPassword ? 'text' : 'password'}
              error={error}
              autoComplete="new-password"
              inputProps={{
                autoComplete: 'new-password',
                form: {
                  autoComplete: 'new-password',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={toggleShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) => validatePassword(e.target.value)}
              onFocus={showValidationList}
              onBlur={hideValidationList}
              label={label || (t('regPassword') as string)}
            />
          )}
        />
        {error && errorMessage && (
          <FormHelperText error>{errorMessage}</FormHelperText>
        )}
        {showValidations && (
          <PasswordValidationList
            validations={isPasswordValid}
            show={showValidationMessages || error || false}
          />
        )}
      </FormControl>

      {confirmFieldName && (
        <FormControl fullWidth className={styles.confirmPasswordContainer}>
          <Controller
            name={confirmFieldName}
            control={control}
            rules={{ required: true }}
            render={({ field: { ref, ...fieldProps } }) => (
              <TextField
                {...fieldProps}
                id={confirmFieldName}
                type={showConfirmPassword ? 'text' : 'password'}
                error={confirmError || isConfirmed}
                autoComplete="new-password"
                inputProps={{
                  autoComplete: 'new-password',
                  form: {
                    autoComplete: 'new-password',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={toggleShowConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => validateConfirmPassword(e.target.value)}
                label={confirmLabel || t('regConfirmPassword')}
              />
            )}
          />
          {(confirmError || isConfirmed) && (
            <FormHelperText error>
              {confirmErrorMessage || t('regConfirmPasswordValidation')}
            </FormHelperText>
          )}
        </FormControl>
      )}
    </>
  );
};

export default PasswordInput;
